import { ListedInstrument, ListedInstrumentWithId, removeDuplicatesByKey } from '@eih-netbroker/common';
import { LinearProgress, Popover } from '@mui/material';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-line.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/information-fill.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-line.svg';
import { useDialog } from '../../../context/DialogContext';
import { useLocale } from '../../../context/NbLocalizationContext';
import { ProductDetailsModal } from '../../../modules/trading/components/ProductDetailsModal';
import { PriceAlertEditModal } from '../../../modules/trading/components/price-alert/PriceAlertEditModal';
import { useOrderModal } from '../../../modules/trading/services/order/useOrderModal';
import { useInstrumentSearch } from '../../services/useInstrumentSearch';
import { useDebounce } from '../../utils/useDebounce';
import { uuid } from '../../utils/uuidUtils';
import { LazyChart } from '../chartIq/LazyChart';
import { NbIconButton } from '../icon-button/IconButton';
import { InstrumentResultRow } from '../instrument-result-row/InstrumentResultRow';
import { InstrumentSearchModalWatchlistPopover } from '../instrument-search-modal-watchlist-popover/InstrumentSearchModalWatchlistPopover';
import { NbTextField } from '../text-field/TextField';
import classes from './InstrumentSearchModal.module.scss';

export interface InstrumentSearchModalProps {
  onSelect?: (instrument: ListedInstrument) => void;
  onCancel: () => void;
  isWithActions?: boolean;
}

export const InstrumentSearchModal = ({ onSelect, onCancel, isWithActions = true }: InstrumentSearchModalProps) => {
  const { translation } = useLocale();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [resultList, setResultList] = useState<Array<ListedInstrumentWithId>>();
  const [searchFinished, setSearchFinished] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<boolean>(false);
  const [watchlistPopover, setWatchlistPopover] = useState<HTMLButtonElement | null>(null);
  const [selectedInstrument, setSelectedInstrument] = useState<ListedInstrument | null>(null);
  const { current: transactionId } = useRef<string>(uuid());

  const { debounce, isLoading, cancelDebounce } = useDebounce(instumentList => {
    const instrumentListWithId = (instumentList as Array<ListedInstrument>).map(instrument => ({
      ...instrument,
      id: `${instrument.ric}-${instrument.isin}-${instrument.ticker}-${instrument.mic}-${instrument.instrumentCategory}`,
    }));
    setResultList(removeDuplicatesByKey(instrumentListWithId, 'id'));
  });
  const { searchInstrument } = useInstrumentSearch();
  const { openDialog } = useDialog();
  const { openOrderModal } = useOrderModal();

  const onSearch = useCallback(
    async (text: string) => {
      setSearchTerm(text);
      if (text.length === 0) {
        setSearchFinished(false);
      }
    },
    [setSearchTerm, setSearchFinished],
  );

  const handleClickOnOrder = useCallback(
    async (instrument: ListedInstrument) => {
      await openOrderModal({ instrument }, undefined, transactionId);
    },
    [openOrderModal, transactionId],
  );

  const handleClickOnChart = useCallback(
    async (instrument: ListedInstrument) => {
      openDialog((close: () => void) => (
        <LazyChart
          type={'modal'}
          props={{ instrument, onCancel: close }}
        />
      ));
    },
    [openDialog],
  );

  const handleClickOnProductDetails = useCallback(
    async (instrument: ListedInstrument) => {
      await openDialog((close: () => void) => (
        <ProductDetailsModal
          onCancel={close}
          instrument={instrument}
        />
      ));
    },
    [openDialog],
  );

  const handleClickOnWatchlist = useCallback(
    async (event: MouseEvent<HTMLButtonElement>, instrument: ListedInstrument) => {
      setWatchlistPopover(event.currentTarget);
      setSelectedInstrument(instrument);
    },
    [setWatchlistPopover, setSelectedInstrument],
  );

  const handleClickOnNotification = useCallback(
    async (instrument: ListedInstrument) => {
      await openDialog((close: () => void) => (
        <PriceAlertEditModal
          onCancel={() => close()}
          instrument={instrument}
          isFromPriceAlertsWidget={false}
        />
      ));
    },
    [openDialog],
  );

  useEffect(() => {
    setSearchFinished(false);
    setSearchError(false);

    if (!searchTerm.trim()) {
      cancelDebounce();
      setResultList([]);
    } else {
      debounce(async () => {
        try {
          return await searchInstrument(searchTerm, undefined, transactionId);
        } catch (error) {
          setSearchError(true);
        } finally {
          setSearchFinished(true);
        }
      });
    }
  }, [searchTerm, debounce, searchInstrument, cancelDebounce, transactionId]);

  return (
    <div className={classes.search}>
      <div className={classes.header}>
        <h2 data-testid="instrument-search-modal-title">{translation.instrument.search.title}</h2>
        <NbIconButton
          data-testid="instrument-search-modal-close-button"
          icon={<CloseIcon />}
          onClick={onCancel}
        />
      </div>

      <div className={classes.input}>
        <NbTextField
          size={'small'}
          autoFocus
          value={searchTerm}
          onChange={e => onSearch(e.target.value)}
          placeholder={translation.instrument.search.instruments}
          InputProps={{ startAdornment: <SearchIcon /> }}
          inputProps={{ 'data-testid': 'instrument-search-modal-input' }}
        />
      </div>

      {isLoading && <LinearProgress className={classes.progressBar} />}

      {searchFinished && searchError && searchTerm.trim().length > 0 && !resultList && (
        <div className={classes.noOption}>
          <InfoIcon /> {translation.instrument.search.errorResult}
        </div>
      )}

      {searchFinished && !searchError && searchTerm.trim().length > 0 && resultList && resultList.length === 0 && (
        <div className={classes.noOption}>
          <InfoIcon /> {translation.instrument.search.noResult}
        </div>
      )}

      <div className={classes.listWrapper}>
        <ul
          data-testid="instrument-search-modal-result-list"
          className={classes.list}
        >
          {resultList?.map(instrument => (
            <li
              key={instrument.id}
              data-testid={`instrument-search-result-item-${instrument.isin}`}
            >
              <InstrumentResultRow
                isWithActions={isWithActions}
                instrument={instrument}
                onClick={() => (onSelect ? onSelect(instrument) : {})}
                onOrderClick={handleClickOnOrder}
                onNotificationClick={handleClickOnNotification}
                onChartClick={handleClickOnChart}
                onProductDetailsClick={handleClickOnProductDetails}
                onWatchlistClick={handleClickOnWatchlist}
              />
            </li>
          ))}
        </ul>
      </div>

      <Popover
        open={!!watchlistPopover}
        anchorEl={watchlistPopover}
        onClose={() => setWatchlistPopover(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        classes={{ paper: classes.popover }}
      >
        <InstrumentSearchModalWatchlistPopover instrument={selectedInstrument} />
      </Popover>
    </div>
  );
};
